import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import {
    isCompleteLandmarkPair,
    LandmarkResidual,
} from '~/domain/landmark/landmark';
import { sampleAggregate } from '~/domain/statistics/sampleAggregate';
import ResidualScaleNote from '~/components/Landmarks/ResidualScaleNote';
import { useLandmarkController } from '~/hooks/useLandmarkController';
import { useLandmarkReadout } from '~/hooks/viewer/useLandmarkReadout';
import { landmarkPairListState } from '~/state/atoms/landmarkAtoms';
import {
    formatMeanWithSpreadCm,
    NOT_AVAILABLE,
} from '~/utils/measurementFormat';
import {
    TransformResiduals,
    useRunLandmarkResiduals,
} from './useRunLandmarkResiduals';

// The name takes the room the two value columns don't; they are only as wide as the mean ± SD summary needs.
const GRID = 'minmax(0, 1fr) 62px 62px';
const GAP = 0.75;

function byPairId(
    transform: TransformResiduals | undefined
): ReadonlyMap<string, LandmarkResidual> {
    return new Map((transform?.residuals ?? []).map((r) => [r.pairId, r]));
}

function Value({
    residual,
    textOf,
}: {
    residual?: LandmarkResidual;
    textOf: (r: LandmarkResidual) => string;
}) {
    return (
        <Typography
            variant="caption"
            noWrap
            sx={{
                textAlign: 'right',
                color:
                    residual?.status === 'measured'
                        ? undefined
                        : 'text.secondary',
            }}
        >
            {residual && residual.status === 'measured'
                ? textOf(residual)
                : NOT_AVAILABLE}
        </Typography>
    );
}

function MeanValue({ measuredMm }: { measuredMm: readonly number[] }) {
    const mean = sampleAggregate([...measuredMm]);
    return (
        <Typography
            variant="caption"
            noWrap
            sx={{ textAlign: 'right', fontSize: 10 }}
        >
            {mean ? formatMeanWithSpreadCm(mean) : NOT_AVAILABLE}
        </Typography>
    );
}

// Target registration error of each landmark pair under both the run's rigid and its deformable transform.
export default function RunTreSection() {
    const pairs = useRecoilValue(landmarkPairListState);
    const { rigid, deformable, scale } = useRunLandmarkResiduals();
    const { selectedPairId, togglePair } = useLandmarkController();
    const { nameOf, textOf } = useLandmarkReadout();

    const rigidById = useMemo(() => byPairId(rigid), [rigid]);
    const deformableById = useMemo(() => byPairId(deformable), [deformable]);
    const complete = useMemo(
        () =>
            pairs
                .map((pair, index) => ({ pair, index }))
                .filter((entry) => isCompleteLandmarkPair(entry.pair)),
        [pairs]
    );

    if (complete.length === 0) {
        return null;
    }

    const headerSx = {
        display: 'grid',
        gridTemplateColumns: GRID,
        gap: GAP,
        alignItems: 'center',
        px: 0.25,
    } as const;

    return (
        <Stack spacing={0.5} data-testid="run-tre">
            <Box sx={headerSx}>
                <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="registrationQa.tre.landmark" />
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{
                        gridColumn: 'span 2',
                        textAlign: 'center',
                        fontSize: 9.5,
                    }}
                >
                    <FormattedMessage id="registrationQa.tre.label" />
                </Typography>
            </Box>
            <Box sx={headerSx}>
                <span />
                <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ textAlign: 'right', fontSize: 9.5 }}
                >
                    <FormattedMessage id="replanning.tre.rigid" />
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ textAlign: 'right', fontSize: 9.5 }}
                >
                    <FormattedMessage id="replanning.tre.deformable" />
                </Typography>
            </Box>
            <Box
                sx={{ maxHeight: 220, overflowY: 'auto', overflowX: 'hidden' }}
            >
                {complete.map(({ pair, index }) => (
                    <Box
                        key={pair.id}
                        onClick={() => togglePair(pair.id)}
                        data-testid="run-tre-row"
                        sx={{
                            ...headerSx,
                            minHeight: 20,
                            cursor: 'pointer',
                            borderRadius: 1,
                            bgcolor:
                                pair.id === selectedPairId
                                    ? 'action.selected'
                                    : undefined,
                        }}
                    >
                        <Typography
                            variant="caption"
                            noWrap
                            title={nameOf(pair, index)}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {nameOf(pair, index)}
                        </Typography>
                        <Value
                            residual={rigidById.get(pair.id)}
                            textOf={textOf}
                        />
                        <Value
                            residual={deformableById.get(pair.id)}
                            textOf={textOf}
                        />
                    </Box>
                ))}
            </Box>
            <Box sx={headerSx}>
                <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="registrationQa.totals.label" />
                </Typography>
                <MeanValue measuredMm={rigid?.measuredMm ?? []} />
                <MeanValue measuredMm={deformable?.measuredMm ?? []} />
            </Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 0.25, fontSize: 10 }}
                data-testid="run-tre-footnote"
            >
                <FormattedMessage id="registrationQa.tre.footnote" />
            </Typography>
            <ResidualScaleNote scale={scale} testIdPrefix="run-tre" />
        </Stack>
    );
}
